import { requireCompletedProfile } from "@hisani/auth";
import { canUseStableFeature, canViewStableFeature, } from "../domain/membership/authorization-policy.js";
import { canAddSealedRecord } from "../domain/horses/horse-lifecycle.js";
import { canLogTrainingOutcome, previousCalendarDay, toCalendarDayUTC, } from "../domain/training/training-entry.js";
import { addLotSchema, bulkAssignTrainingEntriesSchema, createTrainingEntrySchema, editTrainingEntrySchema, getTrainingBoardSchema, getTrainingEntryPrefillSchema, listHorseTrainingTimelineSchema, listLotsSchema, logTrainingEntryOutcomeSchema, moveTrainingEntrySchema, stableConflict, stableForbidden, stableInvalidTransition, stableNotFound, } from "../validation.js";
async function requirePlanner(dependencies, stableId, horsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    if (!canUseStableFeature(membership, "TRAINING_SCHEDULER_PLANNING")) {
        throw stableForbidden();
    }
    return membership;
}
async function requirePlanningViewer(dependencies, stableId, horsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    if (!canViewStableFeature(membership, "TRAINING_SCHEDULER_PLANNING")) {
        throw stableForbidden();
    }
    return membership;
}
async function requireLogger(dependencies, stableId, horsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    if (!canUseStableFeature(membership, "TRAINING_SCHEDULER_LOGGING")) {
        throw stableForbidden();
    }
    return membership;
}
async function requireLoggingViewer(dependencies, stableId, horsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, horsemanId);
    if (membership === null)
        throw stableNotFound();
    if (!canViewStableFeature(membership, "TRAINING_SCHEDULER_LOGGING")) {
        throw stableForbidden();
    }
    return membership;
}
/** A Training Scheduler entry can only be created against a Horse currently
 * accepting new Sealed entries — the same gate Care Records and Stopwatch
 * attachment already use (`horse-lifecycle.ts`: `canAddSealedRecord`). */
async function requireHorseAcceptingTrainingEntries(dependencies, stableId, horseId) {
    const horse = await dependencies.repository.getHorse(stableId, horseId);
    if (horse === null)
        throw stableNotFound();
    if (!canAddSealedRecord(horse)) {
        throw stableInvalidTransition("This horse is not currently accepting new Sealed records.");
    }
}
async function requireLotInStable(dependencies, stableId, lotId) {
    const lots = await dependencies.repository.listLots(stableId, dependencies.clock.now());
    if (!lots.some((lot) => lot.id === lotId)) {
        throw stableInvalidTransition("This Lot does not belong to this Stable.");
    }
}
async function requireActiveRider(dependencies, stableId, riderHorsemanId) {
    const membership = await dependencies.repository.getMembershipContext(stableId, riderHorsemanId);
    if (membership === null) {
        throw stableInvalidTransition("This Rider is not currently an active Stable member.");
    }
}
function findEntryForHorse(entries, horseId) {
    return entries.find((entry) => entry.horseId === horseId);
}
function findEntryAtCell(entries, lotId, riderHorsemanId) {
    return entries.find((entry) => entry.lotId === lotId && entry.riderHorsemanId === riderHorsemanId);
}
export async function listLots(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listLotsSchema.parse(input);
    await requirePlanningViewer(dependencies, parsed.stableId, actor.account.id);
    return dependencies.repository.listLots(parsed.stableId, dependencies.clock.now());
}
export async function addLot(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = addLotSchema.parse(input);
    await requirePlanner(dependencies, parsed.stableId, actor.account.id);
    const now = dependencies.clock.now();
    return dependencies.repository.addLot({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        name: parsed.name,
        now,
    });
}
export async function getTrainingBoard(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getTrainingBoardSchema.parse(input);
    await requireLoggingViewer(dependencies, parsed.stableId, actor.account.id);
    const [lots, riders, entries] = await Promise.all([
        dependencies.repository.listLots(parsed.stableId, dependencies.clock.now()),
        dependencies.repository.listTrainingRiderRoster(parsed.stableId, parsed.day),
        dependencies.repository.listTrainingEntriesForDay(parsed.stableId, parsed.day),
    ]);
    return { day: parsed.day, lots, riders, entries };
}
export async function createTrainingEntry(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = createTrainingEntrySchema.parse(input);
    await requirePlanner(dependencies, parsed.stableId, actor.account.id);
    await requireHorseAcceptingTrainingEntries(dependencies, parsed.stableId, parsed.horseId);
    if (parsed.lotId !== null) {
        await requireLotInStable(dependencies, parsed.stableId, parsed.lotId);
    }
    if (parsed.riderHorsemanId !== null) {
        await requireActiveRider(dependencies, parsed.stableId, parsed.riderHorsemanId);
    }
    const dayEntries = await dependencies.repository.listTrainingEntriesForDay(parsed.stableId, parsed.day);
    if (findEntryForHorse(dayEntries, parsed.horseId) !== undefined) {
        throw stableConflict("This horse already has a Training Scheduler entry for this day.");
    }
    if (parsed.lotId !== null &&
        parsed.riderHorsemanId !== null &&
        findEntryAtCell(dayEntries, parsed.lotId, parsed.riderHorsemanId) !==
            undefined) {
        throw stableConflict("This cell is already assigned for this day.");
    }
    const now = dependencies.clock.now();
    return dependencies.repository.createTrainingEntry({
        id: dependencies.newId(),
        stableId: parsed.stableId,
        horseId: parsed.horseId,
        day: parsed.day,
        workType: parsed.workType,
        distanceOrDuration: parsed.distanceOrDuration,
        instructions: parsed.instructions,
        lotId: parsed.lotId,
        riderHorsemanId: parsed.riderHorsemanId,
        actorId: actor.account.id,
        now,
    });
}
/**
 * A Trainer can bulk-assign the same Work Type/Distance/Instructions to
 * multiple horses at once; every resulting entry is created independently
 * (its own row, no shared mutable template) so editing one later never
 * mutates another (`docs/products/stable.md`, acceptance criterion 10).
 * Ineligible horses (already scheduled that day, or not currently accepting
 * Sealed entries) are skipped rather than failing the whole batch — each
 * horse's outcome is reported independently.
 */
export async function bulkAssignTrainingEntries(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = bulkAssignTrainingEntriesSchema.parse(input);
    await requirePlanner(dependencies, parsed.stableId, actor.account.id);
    const existingEntries = await dependencies.repository.listTrainingEntriesForDay(parsed.stableId, parsed.day);
    const scheduledHorseIds = new Set(existingEntries.map((entry) => entry.horseId));
    const results = [];
    for (const horseId of parsed.horseIds) {
        if (scheduledHorseIds.has(horseId)) {
            results.push({ horseId, status: "ALREADY_SCHEDULED" });
            continue;
        }
        const horse = await dependencies.repository.getHorse(parsed.stableId, horseId);
        if (horse === null || !canAddSealedRecord(horse)) {
            results.push({ horseId, status: "NOT_ACCEPTING_RECORDS" });
            continue;
        }
        const now = dependencies.clock.now();
        const entry = await dependencies.repository.createTrainingEntry({
            id: dependencies.newId(),
            stableId: parsed.stableId,
            horseId,
            day: parsed.day,
            workType: parsed.workType,
            distanceOrDuration: parsed.distanceOrDuration,
            instructions: parsed.instructions,
            lotId: null,
            riderHorsemanId: null,
            actorId: actor.account.id,
            now,
        });
        scheduledHorseIds.add(horseId);
        results.push({ horseId, status: "CREATED", entry });
    }
    return results;
}
export async function moveTrainingEntry(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = moveTrainingEntrySchema.parse(input);
    await requirePlanner(dependencies, parsed.stableId, actor.account.id);
    const current = await dependencies.repository.getTrainingEntry(parsed.stableId, parsed.entryId);
    if (current === null)
        throw stableNotFound();
    if (parsed.targetLotId !== null) {
        await requireLotInStable(dependencies, parsed.stableId, parsed.targetLotId);
    }
    if (parsed.targetRiderHorsemanId !== null) {
        await requireActiveRider(dependencies, parsed.stableId, parsed.targetRiderHorsemanId);
    }
    const now = dependencies.clock.now();
    const result = await dependencies.repository.moveTrainingEntry({
        stableId: parsed.stableId,
        entryId: parsed.entryId,
        expectedVersion: parsed.expectedVersion,
        targetLotId: parsed.targetLotId,
        targetRiderHorsemanId: parsed.targetRiderHorsemanId,
        now,
    });
    if (result === null)
        throw stableConflict();
    return result;
}
export async function editTrainingEntry(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = editTrainingEntrySchema.parse(input);
    await requirePlanner(dependencies, parsed.stableId, actor.account.id);
    const current = await dependencies.repository.getTrainingEntry(parsed.stableId, parsed.entryId);
    if (current === null)
        throw stableNotFound();
    const now = dependencies.clock.now();
    const entry = await dependencies.repository.editTrainingEntry({
        stableId: parsed.stableId,
        entryId: parsed.entryId,
        expectedVersion: parsed.expectedVersion,
        workType: parsed.workType,
        distanceOrDuration: parsed.distanceOrDuration,
        instructions: parsed.instructions,
        now,
    });
    if (entry === null)
        throw stableConflict();
    return entry;
}
export async function logTrainingEntryOutcome(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = logTrainingEntryOutcomeSchema.parse(input);
    await requireLogger(dependencies, parsed.stableId, actor.account.id);
    const current = await dependencies.repository.getTrainingEntry(parsed.stableId, parsed.entryId);
    if (current === null)
        throw stableNotFound();
    const today = toCalendarDayUTC(dependencies.clock.now());
    if (!canLogTrainingOutcome(current.day, today)) {
        throw stableInvalidTransition("This entry's scheduled day has not arrived yet.");
    }
    const now = dependencies.clock.now();
    const entry = await dependencies.repository.logTrainingEntryOutcome({
        stableId: parsed.stableId,
        entryId: parsed.entryId,
        expectedVersion: parsed.expectedVersion,
        actorId: actor.account.id,
        outcomeNotes: parsed.outcomeNotes,
        now,
    });
    if (entry === null)
        throw stableConflict();
    return entry;
}
export async function getTrainingEntryPrefill(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = getTrainingEntryPrefillSchema.parse(input);
    await requirePlanner(dependencies, parsed.stableId, actor.account.id);
    const previousDay = previousCalendarDay(parsed.day);
    const previous = await dependencies.repository.getEntryForHorseOnDay(parsed.stableId, parsed.horseId, previousDay);
    if (previous === null) {
        return { workType: null, distanceOrDuration: null, instructions: null };
    }
    const riderLeftStable = previous.entry.riderHorsemanId !== null && !previous.riderActiveMember;
    if (riderLeftStable) {
        return { workType: null, distanceOrDuration: null, instructions: null };
    }
    return {
        workType: previous.entry.workType,
        distanceOrDuration: previous.entry.distanceOrDuration,
        instructions: previous.entry.instructions,
    };
}
export async function listHorseTrainingTimeline(principal, input, dependencies) {
    const actor = requireCompletedProfile(principal);
    const parsed = listHorseTrainingTimelineSchema.parse(input);
    await requireLoggingViewer(dependencies, parsed.stableId, actor.account.id);
    return dependencies.repository.listHorseTrainingTimeline(parsed);
}
